// Copyright 2016 Sorama B.V.

import { autoinject, BindingEngine } from "aurelia-framework";
import { HttpResponseMessage, RequestMessage } from "aurelia-http-client";
import { CreditMutationType, CreditsDebetAction } from "datatypes/CreditsMutation";
import { parseUri } from "extensions/UriExtensions";
import { IAuthentication, IAuthenticationHandler } from "libraries/Authentication/IAuthentication";
import { Api } from "libraries/WebApi/Api";
import { IWebRequestHandler, IWebRequestInterceptorHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { CreditsBalanceRepository } from "repositories/CreditsBalanceRepository";
import { ICreditsService } from "services/Credits/ICreditsService";
import { INotificationService, NotificationUrgency } from "services/Notification/INotificationService";

@autoinject
export class CreditsService implements ICreditsService, IAuthenticationHandler, IWebRequestInterceptorHandler {
    private static SoramaCreditsSaldoHeader = "Sorama-CreditSaldo";
    private static SoramaCreditsDebetCostsHeader = "Sorama-Costs";
    private static CachedCreditsAmount = "CachedCreditsAmount";
    private static PendingCreditsAmount = "PendingCreditsAmount";
    private static PendingCreditsList = "PendingCreditsList";
    private static readonly NotificationSourceName = "Sorama Credits";

    private authentication: IAuthentication;
    private bindingEngine: BindingEngine;
    private creditsBalanceRepository: CreditsBalanceRepository;
    private webRequestHandler: IWebRequestHandler;
    private notificationService: INotificationService;

    private creditsWarningThreshold = 50; // Arbitraty value, discussed with sales team.
    private creditsUpdateThreshold = -100;
    private creditsUpdateTimeThresholdMs: number = 10 * 1000;
    private subscription: unknown;
    private creditTimeoutId: number;

    // Cached properties
    private set creditsPendingAmount(amount: number) {
        localStorage.setItem(CreditsService.PendingCreditsAmount, amount.toString());
    }
    private get creditsPendingAmount(): number {
        const result = Number(localStorage.getItem(CreditsService.PendingCreditsAmount));
        return result ? result : 0;
    }
    public set currentCreditSaldo(amount: number) {
        localStorage.setItem(CreditsService.CachedCreditsAmount, amount.toString());

        if (amount < this.creditsWarningThreshold) {
            const msg = "Sorama Credits (almost) depleted.";
            const idx = this.notificationService.notifications.findIndex(
                (x) => x.source == CreditsService.NotificationSourceName && x.message === msg
            );

            if (idx == -1)
                this.notificationService.registerNotification(
                    CreditsService.NotificationSourceName,
                    msg,
                    NotificationUrgency.Warning
                );
        }
    }
    public get currentCreditSaldo(): number {
        const result = Number(localStorage.getItem(CreditsService.CachedCreditsAmount));
        return result ? Math.round(result + this.creditsPendingAmount) : 0;
    }

    private creditsMutationList: CreditsDebetAction[];

    constructor(
        webRequestHandler: IWebRequestHandler,
        creditsBalanceRepository: CreditsBalanceRepository,
        authentication: IAuthentication,
        notificationService: INotificationService,
        bindingEngine: BindingEngine
    ) {
        this.authentication = authentication;
        this.creditsBalanceRepository = creditsBalanceRepository;
        this.bindingEngine = bindingEngine;
        this.webRequestHandler = webRequestHandler;
        this.notificationService = notificationService;

        // Load list
        const list = localStorage.getItem(CreditsService.PendingCreditsList);
        this.creditsMutationList = list ? JSON.parse(list) : [];

        if (this.creditsMutationList.length == 0) this.creditsPendingAmount = 0;

        this.subscription = this.bindingEngine
            .collectionObserver(this.creditsMutationList)
            .subscribe(() =>
                localStorage.setItem(CreditsService.PendingCreditsList, JSON.stringify(this.creditsMutationList))
            );

        // Add interceptor for the sorama domain
        const baseUri = parseUri(Api.baseUri);
        const computeUri = parseUri(Api.computeUri);

        this.webRequestHandler.addInterceptor(baseUri.host, this);
        this.webRequestHandler.addInterceptor(computeUri.host, this);

        // Refresh credits when login in
        this.authentication.addAuthenticationHandler(this);

        if (this.authentication.isSignedIn) {
            this.updateCurrentCreditsSaldo();
        }
    }

    public updateCurrentCreditsSaldo(): Promise<number> {
        return this.creditsBalanceRepository
            .getSaldoForCreditsBalance()
            .then((x) => {
                return (this.currentCreditSaldo = x);
            })
            .catch(() => {
                // We're probably offline, so return last known saldo
                return this.currentCreditSaldo;
            });
    }

    public creditSaldoMutation(creditDebetAction: CreditsDebetAction): Promise<void> {
        if (this.currentCreditSaldo < -creditDebetAction.amount) return Promise.reject("Out of credits");

        // Add to pending transaction list or merge with pending request if possible
        this.addCredistTransactionToList(creditDebetAction);

        this.creditsPendingAmount += creditDebetAction.amount;

        // Only update when threshold is exceeded, to minimize server load (is also updated after a timeout).
        if (
            this.creditsMutationList.map((x) => x.amount).reduce((acc, val) => acc + val) <= this.creditsUpdateThreshold
        ) {
            return this.pushPendingDebetActions();
        } else if (!this.creditTimeoutId) {
            this.creditTimeoutId = window.setTimeout(() => {
                this.pushPendingDebetActions();
            }, this.creditsUpdateTimeThresholdMs);
        }

        return Promise.resolve();
    }

    public getDebetActionCosts(): CreditsDebetAction[] {
        return [
            {
                amount: 0, // cost per frame @ 25 fps
                creditDebetType: CreditMutationType.Spectrum,
                describtion: "Real-time Spectrum",
                mergable: true,
            },
            {
                amount: 0, // cost per frame @ 25 fps
                creditDebetType: CreditMutationType.NearField,
                describtion: "Real-time Near-field",
                mergable: true,
            },
            {
                amount: 0, // cost per frame @ 25 fps
                creditDebetType: CreditMutationType.Beamforming,
                describtion: "Real-time Beamforming",
                mergable: true,
            },
        ];
    }

    public onSignIn(userId: string): Promise<void> {
        return this.updateCurrentCreditsSaldo().then(() => {
            // We're up and running agin so process failed transactions again
            this.pushPendingDebetActions();
            return;
        });
    }

    public onSignOut(): Promise<void> {
        return Promise.resolve();
    }

    public onSendRequest(request: RequestMessage): Promise<void> {
        return Promise.resolve();
    }

    public onReceiveResponse(response: HttpResponseMessage): Promise<void> {
        // Update the credit saldo, if it contains an update
        if (response.headers.has(CreditsService.SoramaCreditsSaldoHeader)) {
            const val = response.headers.get(CreditsService.SoramaCreditsSaldoHeader);
            this.currentCreditSaldo = Number(val);
        }

        return Promise.resolve();
    }

    public onResponseError(response: HttpResponseMessage): Promise<boolean> {
        if (response.statusCode === 402) {
            // payment required for completing task, so show a notification.
            this.notificationService.registerNotification(
                CreditsService.NotificationSourceName,
                response.content.message,
                NotificationUrgency.Error
            );
        }

        return Promise.resolve(false);
    }

    private pushPendingDebetActions(): Promise<void> {
        if (!this.creditsMutationList || this.creditsMutationList.length <= 0) {
            // Iterating over an empty list apparantly crashes in IE
            return Promise.resolve();
        }

        // Clear list & timer
        const mutationList = this.creditsMutationList.splice(0, this.creditsMutationList.length);
        if (this.creditTimeoutId) {
            window.clearTimeout(this.creditTimeoutId);
            this.creditTimeoutId = null;
        }

        // Send Credit requests
        const pendingList: Promise<void>[] = [];
        while (mutationList.length > 0) {
            const action = mutationList.pop();

            pendingList.push(
                this.creditsBalanceRepository
                    .postCreditsDebetAction(action.amount, action.describtion)
                    .then(() => {
                        // Update pendingAmount
                        this.creditsPendingAmount = Math.min(this.creditsPendingAmount - action.amount, 0);
                    })
                    .catch((error) => {
                        // Failed, so back in the list
                        this.addCredistTransactionToList(action);
                    })
            );
        }

        // Wait for completion
        return Promise.all(pendingList).then(() => {
            this.updateCurrentCreditsSaldo();
            return;
        });
    }

    private addCredistTransactionToList(creditDebetAction: CreditsDebetAction) {
        const index = this.creditsMutationList.findIndex(
            (x) =>
                x.creditDebetType === creditDebetAction.creditDebetType &&
                x.mergable &&
                x.describtion === creditDebetAction.describtion
        );
        if (index != -1 && creditDebetAction.mergable) {
            const item = this.creditsMutationList[index];
            item.amount += creditDebetAction.amount;
            this.creditsMutationList.splice(index, 1, item);
        } else {
            this.creditsMutationList.push(creditDebetAction);
        }
    }
}
